import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PageLayout from '@/components/PageLayout';
import SEO from '@/components/SEO';
import { CheckCircle, MessageCircle, Repeat, Target, Gift } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';

const ForRealEstateBrokers = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const valueItems = [
    {
      icon: MessageCircle,
      title: "Stay Top of Mind",
      description: "Automatically stay connected with clients as they receive co-branded tips, reminders, and seasonal advice."
    },
    {
      icon: Repeat,
      title: "Increase Repeat & Referral Business", 
      description: "When clients need to upgrade, refinance, or sell, they already know who to call."
    },
    {
      icon: Target,
      title: "Deliver Ongoing Value",
      description: "Clients get real help managing the home you helped them find and fall in love with."
    },
    {
      icon: Gift,
      title: "Promote Local Partners",
      description: "Feature preferred vendors and home service partners."
    }
  ];

  const steps = [
    {
      title: "Invite Clients",
      description: "Send a custom invite link from your dashboard or embed it in your post-close workflow."
    },
    {
      title: "Clients Onboard", 
      description: "They create their free House Beat account, co-branded with your name and info."
    },
    {
      title: "We Take It From There",
      description: "Clients receive tailored reminders, advice, and planning tools—all tied to your brand and voice."
    },
    {
      title: "Add Your Voice",
      description: "Easily input sponsored content, links, or promotions. We'll dynamically connect your brand to the content that matters most to your clients."
    }
  ];

  const features = [
    "Co-branded and customized onboarding experience", 
    "Sponsored content and promotions input directly by you",
    "Dynamic association of your brand with relevant content"
  ];

  return (
    <PageLayout>
      <SEO 
        title="House Beat for Real Estate Brokers - Turn Homeowners Into Lifelong Clients"
        description="Stay connected after the sale with a co-branded experience that helps your clients manage their homes—and keeps your name tied to every smart decision they make."
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="min-h-screen"
      >
        {/* Hero Section */}
        <section className="relative w-full nav-safe-pt pb-12 md:pb-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Column - Content */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="space-y-6"
              >
                <motion.h1 
                  variants={itemVariants}
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight"
                >
                  Turn Homeowners Into Lifelong Clients<span className="text-green-500">.</span>
                </motion.h1>
                <motion.p 
                  variants={itemVariants}
                  className="text-lg md:text-xl text-gray-600 mt-4"
                >
                  Stay top-of-mind and generate repeat business—automatically. House Beat keeps you connected to your clients with a co-branded platform that supports them long after the sale.
                </motion.p>
                <motion.div variants={itemVariants}>
                  <Button asChild variant="brand" size="lg" className="text-lg px-8 py-4">
                    <a href="https://housebeat.com/book-demo" target="_blank" rel="noopener noreferrer">Book a Demo</a>
                  </Button>
                </motion.div>
              </motion.div>
              {/* Right Column - Image */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px]"
              >
                <OptimizedImage
                  src="/Real Estate Edited.png"
                  alt="House Beat for Real Estate Brokers - Turn Homeowners Into Lifelong Clients"
                  className="w-full h-full object-cover rounded-2xl shadow-2xl"
                  fetchPriority="high"
                  context="hero"
                  width={800}
                  height={600}
                />
              </motion.div>
            </div>
          </div>
        </section>


        {/* Features Section */}
        <motion.section variants={itemVariants} className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-2xl font-bold mb-6">
                A Smart, Co-Branded Platform for Homeowners—Powered by You
              </h2>
              <p className="text-lg text-on-gradient">
                Most homeowners feel overwhelmed by maintenance, unexpected costs, and project planning. They often turn to the person they trust most: their real estate agent, for help long after closing.
              </p>
              <p className="text-lg text-on-gradient mt-4">
                House Beat gives your clients a personalized dashboard that makes it easy to track maintenance, plan upgrades, and stay on top of their home's health—all with your name, face, and expertise integrated throughout.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="text-center h-full transition-all duration-300 hover:shadow-lg hover:shadow-brand-green/20 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <CheckCircle className="h-12 w-12 mx-auto text-brand-green" />
                    </div>
                    <p className="text-muted-foreground">{feature}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Value Grid */}
        <motion.section variants={itemVariants} className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-12">
              How You Win
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {valueItems.map((item, index) => (
                <Card key={index} className="text-center h-full transition-all duration-300 hover:shadow-lg hover:shadow-brand-green/20 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <item.icon className="h-12 w-12 mx-auto text-brand-blue" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Steps Section */}
        <motion.section variants={itemVariants} className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-12">
              It's Easy to Get Started
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {steps.map((step, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-brand-blue text-brand-blue-foreground rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                      <p className="text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-8">
                <Button size="lg" variant="brand" className="text-lg" onClick={() => window.open('https://housebeat.com/book-demo', '_blank')}>
                  Book a Demo
                </Button>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Testimonial Section */}
        <motion.section variants={itemVariants} className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <blockquote className="text-lg md:text-xl font-light italic text-muted-foreground mb-6">
                "Our goal is to have House Beat help top brokers and agents stay top of mind without monthly newsletters or chasing people down."
              </blockquote>
              <cite className="text-lg font-semibold">
                — Paul Crowe, House Beat CEO
              </cite>
            </div>
          </div>
        </motion.section>

        {/* Final CTA */}
        <motion.section variants={itemVariants} className="py-20 bg-gradient-to-br from-brand-green to-brand-green/80 text-brand-green-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-6">
              Be More Than Their Agent. Be Their Go-To for Homeownership.
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
              Your clients need help after the close. House Beat lets you deliver it—without more work.
            </p>
            <Button size="lg" variant="brand" className="text-lg" onClick={() => window.open('https://housebeat.com/book-demo', '_blank')}>
              Schedule a Demo
            </Button>
          </div>
        </motion.section>
      </motion.div>
    </PageLayout>
  );
};

export default ForRealEstateBrokers;